import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen, HelpCircle, X, Maximize2, ExternalLink } from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import { images } from "../../assets/images";

const OPTION_LABELS = ["A", "B", "C", "D"];

// ─── Embed helpers ────────────────────────────────────────────────────────────

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^?&\s]{11})/,
  );
  return m ? m[1] : null;
};

const getVimeoId = (url) => {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

const getEmbedMeta = (url) => {
  if (!url) return null;
  const ytId = getYouTubeId(url);
  if (ytId)
    return {
      type: "youtube",
      thumbnail: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`,
      embedUrl: `https://www.youtube.com/embed/${ytId}?autoplay=1`,
    };
  const vimeoId = getVimeoId(url);
  if (vimeoId)
    return {
      type: "vimeo",
      thumbnail: `https://vumbnail.com/${vimeoId}.jpg`,
      embedUrl: `https://player.vimeo.com/video/${vimeoId}?autoplay=1`,
    };
  return null;
};

// ─── Media Lightbox ───────────────────────────────────────────────────────────

const MediaLightbox = ({ media, onClose }) => {
  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div className="rm-lb-overlay" onClick={onClose}>
      <div className="rm-lb-box" onClick={(e) => e.stopPropagation()}>
        <button className="rm-lb-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
        {media.type === "image" && (
          <img src={media.src} alt="Full preview" className="rm-lb-img" />
        )}
        {media.type === "video" && (
          <video src={media.src} controls autoPlay className="rm-lb-video" />
        )}
        {media.type === "embed" && (
          <div className="rm-lb-iframe-wrap">
            <iframe
              src={media.src}
              title="Video"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              frameBorder="0"
              className="rm-lb-iframe"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Embed card (YouTube / Vimeo) ─────────────────────────────────────────────

const EmbedCard = ({ meta, onOpenLightbox }) => (
  <div className="rm-media-card mb-4">
    <div
      className="rm-thumb-wrap"
      onClick={() => onOpenLightbox({ type: "embed", src: meta.embedUrl })}
    >
      <img
        src={meta.thumbnail || images.placeHolderImg}
        alt="video preview"
        className="rm-thumb-img"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = images.placeHolderImg;
        }}
      />
      <div className="rm-thumb-dim" />
      <button className="rm-play-btn" aria-label="Play">
        <i className="fa-solid fa-play" />
      </button>
      <span className={`rm-platform-tag rm-platform-tag--${meta.type}`}>
        {meta.type === "youtube" ? (
          <>
            <i className="fa-brands fa-youtube" /> YouTube
          </>
        ) : (
          <>
            <i className="fa-brands fa-vimeo-v" /> Vimeo
          </>
        )}
      </span>
    </div>
  </div>
);

// ─── Direct video player ──────────────────────────────────────────────────────

const VideoFileCard = ({ src, thumbnail, onOpenLightbox }) => {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="rm-media-card mb-4">
      {!playing ? (
        <div className="rm-thumb-wrap" onClick={() => setPlaying(true)}>
          <img
            src={thumbnail || images.placeHolderImg}
            alt="video thumbnail"
            className="rm-thumb-img"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = images.placeHolderImg;
            }}
          />
          <div className="rm-thumb-dim" />
          <button className="rm-play-btn" aria-label="Play video">
            <i className="fa-solid fa-play" />
          </button>
          <span className="rm-video-hint">Click to play</span>
        </div>
      ) : (
        <div className="rm-video-player-wrap">
          <video src={src} controls autoPlay className="rm-video-el" />
          <button
            className="rm-expand-btn"
            onClick={() => onOpenLightbox({ type: "video", src })}
            title="Fullscreen"
            aria-label="Fullscreen"
          >
            <Maximize2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Content block ────────────────────────────────────────────────────────────

const ContentBlock = ({ block, onOpenLightbox }) => {
  if (block.content_type === "text") {
    return (
      <div
        className="rm-text-block mb-4"
        dangerouslySetInnerHTML={{ __html: block.content }}
      />
    );
  }

  if (block.content_type === "image") {
    return (
      <div
        className="rm-media-card rm-img-card mb-4"
        role="button"
        tabIndex={0}
        onClick={() => onOpenLightbox({ type: "image", src: block.image })}
        onKeyDown={(e) =>
          e.key === "Enter" &&
          onOpenLightbox({ type: "image", src: block.image })
        }
      >
        <img
          src={block.image}
          alt="chapter visual"
          className="rm-img-el"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = images.placeHolderImg;
          }}
        />
        <div className="rm-img-hover-overlay">
          <div className="rm-img-zoom-icon">
            <Maximize2 size={22} />
          </div>
        </div>
      </div>
    );
  }

  if (block.content_type === "video") {
    const meta = getEmbedMeta(block.video);
    if (meta) return <EmbedCard meta={meta} onOpenLightbox={onOpenLightbox} />;
    return (
      <VideoFileCard
        src={block.video}
        thumbnail={block.thumbnail}
        onOpenLightbox={onOpenLightbox}
      />
    );
  }

  if (block.content_type === "video_link" || block.content_type === "link") {
    const url =
      block.video_link || block.link || block.url || block.content || "";
    const meta = getEmbedMeta(url);
    if (meta) return <EmbedCard meta={meta} onOpenLightbox={onOpenLightbox} />;
    return (
      <div className="rm-link-card mb-4">
        <ExternalLink size={14} className="me-2 flex-shrink-0" />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-truncate"
        >
          {url}
        </a>
      </div>
    );
  }

  return null;
};

// ─── Chapter panel ────────────────────────────────────────────────────────────

const ChapterPanel = ({
  item,
  contentBlocks,
  loadingContent,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  isCompleted,
  onComplete,
  startTime,
}) => {
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = useCallback((media) => setLightbox(media), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  return (
    <div className="course-detail-content">
      {lightbox && <MediaLightbox media={lightbox} onClose={closeLightbox} />}

      <h6 className="course-detail-section-title">{item.title}</h6>

      {loadingContent ? (
        <div className="text-center py-5 text-muted">
          <div
            className="spinner-border spinner-border-sm me-2"
            role="status"
          />
          <span className="fs-14">Loading content...</span>
        </div>
      ) : contentBlocks.length > 0 ? (
        [...contentBlocks]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((block, i) => (
            <ContentBlock key={i} block={block} onOpenLightbox={openLightbox} />
          ))
      ) : (
        <p className="text-muted fs-14">
          No content available for this chapter.
        </p>
      )}

      <div className="d-flex justify-content-center my-4">
        <button
          className={`btn px-4 d-flex align-items-center gap-2 ${
            isCompleted ? "btn-success" : "btn-outline-success"
          }`}
          onClick={() =>
            onComplete(Math.floor((Date.now() - startTime) / 1000))
          }
          disabled={isCompleted}
        >
          <i
            className={`fa-solid ${isCompleted ? "fa-circle-check" : "fa-check"}`}
          />
          {isCompleted ? "Completed" : "Mark as Completed"}
        </button>
      </div>

      <div className="course-detail-actions">
        <button
          className="btn btn-outline-primary d-flex align-items-center gap-2"
          onClick={onPrev}
          disabled={!hasPrev}
        >
          <i className="fa-solid fa-chevron-left" /> Previous
        </button>
        <button
          className="btn btn-outline-primary d-flex align-items-center gap-2"
          onClick={onNext}
          disabled={!hasNext}
        >
          Next <i className="fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  );
};

// ─── Quiz panel ───────────────────────────────────────────────────────────────

const QuizPanel = ({
  item,
  quizData,
  loadingContent,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onComplete,
}) => {
  const { submitTest, markCompleted, getModuleDetail } = useGlobal();

  const questions = quizData?.questions || [];
  const test_pid = quizData?.test_pid || "";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const quizStartTime = useRef(Date.now());

  useEffect(() => {
    setCurrentIndex(0);
    setAnswers({});
    setSubmitted({});
    setScoreData(null);
    quizStartTime.current = Date.now();
  }, [item.id]);

  useEffect(() => {
    if (!quizData?.is_submitted || !quizData.questions?.length) return;
    const preAnswers = {};
    const preSubmitted = {};
    quizData.questions.forEach((q, i) => {
      preAnswers[q.id] = q.selected_answer_id;
      preSubmitted[i] = true;
    });
    setAnswers(preAnswers);
    setSubmitted(preSubmitted);
    setScoreData({
      is_pass: quizData.is_pass,
      marks_obtained: quizData.marks_obtained,
      correct_answer_count: quizData.correct_answer_count,
      wrong_answer_count: quizData.wrong_answer_count,
    });
  }, [quizData]);

  if (loadingContent) {
    return (
      <div className="course-detail-content text-center py-5 text-muted">
        <div className="spinner-border spinner-border-sm me-2" role="status" />
        <span className="fs-14">Loading quiz...</span>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="course-detail-content text-center py-5 text-muted">
        <HelpCircle size={40} className="mb-2 opacity-50" />
        <p className="fs-14">No questions available for this quiz.</p>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const isSubmitted = !!submitted[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;

  const correctAnswerId =
    currentQ.ans_json.find((a) => a.is_correct)?.id ?? null;

  const selectedAnswerId = answers[currentQ.id];

  const isCorrect = isSubmitted && selectedAnswerId === correctAnswerId;

  const handleSelect = (optionId) => {
    if (isSubmitted) return;

    setAnswers((prev) => ({
      ...prev,
      [currentQ.id]: optionId,
    }));
  };

  const handleSubmitAnswer = () => {
    setSubmitted((prev) => ({ ...prev, [currentIndex]: true }));
  };

  const getOptionState = (optionId) => {
    if (!isSubmitted)
      return selectedAnswerId === optionId ? "selected" : "idle";

    if (optionId === correctAnswerId) return "correct";

    if (optionId === selectedAnswerId) return "wrong";

    return "dimmed";
  };

  const isReviewMode = !!scoreData;

  const handleFinishQuiz = async () => {
    setSubmitting(true);
    const answersPayload = {};
    questions.forEach((q) => {
      if (answers[q.id] !== undefined) {
        answersPayload[String(q.id)] = answers[q.id];
      }
    });
    const res = await submitTest({ test_pid, answers: answersPayload });
    if (res?.status) {
      const time_spent = Math.floor(
        (Date.now() - quizStartTime.current) / 1000,
      );
      await markCompleted({ module_pid: item.id, time_spent });
      onComplete();
      const detailRes = await getModuleDetail(item.id);
      if (detailRes?.data) {
        const d = detailRes.data;
        const preAnswers = {};
        const preSubmitted = {};
        (d.questions || []).forEach((q, i) => {
          preAnswers[q.id] = q.selected_answer_id;
          preSubmitted[i] = true;
        });
        setAnswers(preAnswers);
        setSubmitted(preSubmitted);
        setScoreData({
          is_pass: d.is_pass,
          marks_obtained: d.marks_obtained,
          correct_answer_count: d.correct_answer_count,
          wrong_answer_count: d.wrong_answer_count,
        });
        setCurrentIndex(0);
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="course-detail-content course-detail-quiz-content">
      {/* ── Score result card ── */}
      {isReviewMode && (
        <div
          className={`qsb flex-md-row qsb--${scoreData.is_pass ? "pass" : "fail"}`}
        >
          <div>
            <div className="qsb-icon">
              <i
                className={`fa-solid ${scoreData.is_pass ? "fa-trophy" : "fa-circle-xmark"}`}
              />
            </div>
            <div className="qsb-verdict">
              {scoreData.is_pass ? "Passed!" : "Failed"}
            </div>
          </div>

          <div className="qsb-stats">
            <div className="qsb-chip">
              <span className="qsb-chip-val">{scoreData.marks_obtained}</span>
              <span className="qsb-chip-lbl">Marks</span>
            </div>
            <div className="qsb-divider" />
            <div className="qsb-chip qsb-chip--correct">
              <span className="qsb-chip-val">
                <i className="fa-solid fa-check" />{" "}
                {scoreData.correct_answer_count}
              </span>
              <span className="qsb-chip-lbl">Correct</span>
            </div>
            <div className="qsb-divider" />
            <div className="qsb-chip qsb-chip--wrong">
              <span className="qsb-chip-val">
                <i className="fa-solid fa-xmark" />{" "}
                {scoreData.wrong_answer_count}
              </span>
              <span className="qsb-chip-lbl">Wrong</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Step progress dots ── */}
      <div className="qz-progress course-detail-quiz-progress">
        <div className="quiz-step-dots">
          {questions.map((_, i) => (
            <span
              key={i}
              className={`quiz-step-dot ${
                isReviewMode
                  ? "done"
                  : i < currentIndex
                    ? "done"
                    : i === currentIndex
                      ? "active"
                      : ""
              }`}
            />
          ))}
        </div>
      </div>

      {/* ── Question card ── */}
      <div className="qz-card-wrap">
        <div className="quiz-card">
          <div className="quiz-q-badge">
            <i className="fa-solid fa-lightbulb" />
            Question {currentIndex + 1} of {questions.length}
          </div>

          <h5
            className="quiz-q-text lms-short-desc"
            dangerouslySetInnerHTML={{ __html: currentQ.question_html }}
          />

          <div className="quiz-options">
            {currentQ.ans_json.map((opt, i) => {
              const state = getOptionState(opt.id);
              return (
                <div
                  key={opt.id}
                  className={`quiz-option quiz-option--${state}`}
                  onClick={() => handleSelect(opt.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleSelect(opt.id)}
                >
                  <span className="quiz-option-letter">{OPTION_LABELS[i]}</span>
                  <span className="quiz-option-text">{opt.text}</span>
                  {state === "correct" && (
                    <span className="quiz-option-icon quiz-option-icon--correct">
                      <i className="fa-solid fa-check" />
                    </span>
                  )}
                  {state === "wrong" && (
                    <span className="quiz-option-icon quiz-option-icon--wrong">
                      <i className="fa-solid fa-xmark" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {isSubmitted && (
            <div
              className={`quiz-result-banner quiz-result-banner--${isCorrect ? "correct" : "wrong"}`}
            >
              <div className="quiz-result-icon-wrap">
                <i
                  className={`fa-solid ${isCorrect ? "fa-trophy" : "fa-circle-xmark"}`}
                />
              </div>
              <div className="quiz-result-body">
                <div className="quiz-result-title">
                  {isCorrect ? "Excellent!" : "Not quite right"}
                </div>
                <div className="quiz-result-msg">
                  {isCorrect
                    ? "You nailed it! Keep up the great work."
                    : `The correct answer is: ${currentQ.ans_json.find((a) => a.id === correctAnswerId)?.text}`}
                </div>
              </div>
            </div>
          )}

          <div className="quiz-nav">
            <button
              className="btn btn-outline-secondary d-flex gap-2 align-items-center"
              onClick={() => setCurrentIndex((p) => p - 1)}
              disabled={currentIndex === 0}
            >
              <i className="fa-solid fa-arrow-left" /> Back
            </button>

            {isReviewMode ? (
              !isLastQuestion && (
                <button
                  className="btn btn-primary d-flex gap-2 align-items-center"
                  onClick={() => setCurrentIndex((p) => p + 1)}
                >
                  Next <i className="fa-solid fa-arrow-right" />
                </button>
              )
            ) : !isSubmitted ? (
              <button
                className="btn btn-secondary"
                disabled={!selectedAnswerId}
                onClick={handleSubmitAnswer}
              >
                Submit Answer
              </button>
            ) : isLastQuestion ? (
              <button
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={handleFinishQuiz}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    />{" "}
                    Submitting…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-flag-checkered" /> Finish Quiz
                  </>
                )}
              </button>
            ) : (
              <button
                className="btn btn-primary d-flex gap-2 align-items-center"
                onClick={() => setCurrentIndex((p) => p + 1)}
              >
                Next <i className="fa-solid fa-arrow-right" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="course-detail-actions mt-4">
        <button
          className="btn btn-outline-primary d-flex align-items-center gap-2"
          onClick={onPrev}
          disabled={!hasPrev}
        >
          <i className="fa-solid fa-chevron-left" /> Previous
        </button>
        <button
          className="btn btn-outline-primary d-flex align-items-center gap-2"
          onClick={onNext}
          disabled={!hasNext}
        >
          Next <i className="fa-solid fa-chevron-right" />
        </button>
      </div>
    </div>
  );
};

// ─── Main ReadMore page ───────────────────────────────────────────────────────

const ReadMore = () => {
  const location = useLocation();
  const { getCourseChapters, getModuleDetail, markCompleted } = useGlobal();
  const course_pid = location.state?.course_pid;

  const [courseItems, setCourseItems] = useState([]);
  const [courseTitle, setCourseTitle] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [completedIds, setCompletedIds] = useState(new Set());

  const [contentBlocks, setContentBlocks] = useState([]);
  const [quizData, setQuizData] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  const navItemRefs = useRef([]);
  const moduleStartTime = useRef(Date.now());

  useEffect(() => {
    if (!course_pid) return;
    getCourseChapters(course_pid).then((res) => {
      if (!res?.data) return;
      const { course_title, modules = [] } = res.data;
      setCourseTitle(course_title || "");
      const items = modules.map((mod) => ({
        id: mod.module_pid,
        title: mod.module_name,
        type: mod.module_type === "quiz" ? "quiz" : "chapter",
        is_completed: mod.is_completed === 1,
      }));
      setCourseItems(items);
      const alreadyDone = new Set(
        items.filter((i) => i.is_completed).map((i) => i.id),
      );
      setCompletedIds(alreadyDone);
    });
  }, [course_pid, getCourseChapters]);

  const selectedItem = courseItems[selectedIndex];

  useEffect(() => {
    if (!selectedItem) return;
    setLoadingContent(true);
    setContentBlocks([]);
    setQuizData(null);
    moduleStartTime.current = Date.now();
    getModuleDetail(selectedItem.id).then((res) => {
      if (res?.module_type === "quiz") {
        setQuizData(res.data || null);
      } else {
        setContentBlocks(res?.data || []);
      }
      setLoadingContent(false);
    });
  }, [selectedItem, getModuleDetail]);

  const goTo = (index) => setSelectedIndex(index);
  const goPrev = () => {
    if (selectedIndex > 0) setSelectedIndex((p) => p - 1);
  };
  const goNext = () => {
    if (selectedIndex < courseItems.length - 1) setSelectedIndex((p) => p + 1);
  };
  const markComplete = async (id, time_spent) => {
    await markCompleted({ module_pid: id, time_spent });
    setCompletedIds((prev) => new Set([...prev, id]));
  };

  useEffect(() => {
    if (window.innerWidth > 991) return;
    navItemRefs.current[selectedIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [selectedIndex]);

  if (!courseItems.length && !loadingContent) {
    return (
      <div className="content-part2 h-100 d-flex flex-column align-items-center justify-content-center py-5 text-muted">
        <BookOpen size={48} className="mb-3 opacity-50" />
        <h3 className="text-muted opacity-50 mb-0">No data found</h3>
      </div>
    );
  }

  return (
    <div className="content-part2">
      <div className="course-detail-topbar">
        <h4 className="course-detail-title">{courseTitle}</h4>
      </div>

      <div className="course-detail-shell">
        <div className="course-detail-sidebar">
          {courseItems.map((item, index) => (
            <div
              key={item.id}
              ref={(el) => {
                navItemRefs.current[index] = el;
              }}
              className={`course-detail-nav-item ${
                index === selectedIndex ? "course-detail-nav-item--active" : ""
              }`}
              onClick={() => goTo(index)}
              role="button"
              style={{ cursor: "pointer" }}
            >
              <span className="course-detail-nav-number">{index + 1}</span>
              <span className="course-detail-nav-text">{item.title}</span>
              {completedIds.has(item.id) ? (
                <i className="fa-solid fa-circle-check text-success flex-shrink-0" />
              ) : item.type === "quiz" ? (
                <HelpCircle size={15} className="text-warning flex-shrink-0" />
              ) : (
                <BookOpen size={15} className="text-primary flex-shrink-0" />
              )}
            </div>
          ))}
        </div>

        <main className="course-detail-main">
          <div className="course-detail-content-head">
            <h5>
              {selectedItem.type === "quiz" ? (
                <>
                  <i className="fa-solid fa-bolt text-warning me-2" />
                  {selectedItem.title}
                </>
              ) : (
                <>
                  <BookOpen size={18} className="text-primary me-2" />
                  {selectedItem.title}
                </>
              )}
            </h5>
            <div className="course-detail-progress">
              <span>
                {selectedIndex + 1} / {courseItems.length}
              </span>
              <button
                className="course-detail-round-btn"
                onClick={goPrev}
                disabled={selectedIndex === 0}
                aria-label="Previous"
              >
                <i className="fa-solid fa-chevron-left" />
              </button>
              <button
                className="course-detail-round-btn course-detail-round-btn--next"
                onClick={goNext}
                disabled={selectedIndex === courseItems.length - 1}
                aria-label="Next"
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          </div>

          {selectedItem.type === "chapter" ? (
            <ChapterPanel
              item={selectedItem}
              contentBlocks={contentBlocks}
              loadingContent={loadingContent}
              onPrev={goPrev}
              onNext={goNext}
              isCompleted={completedIds.has(selectedItem.id)}
              onComplete={(time_spent) =>
                markComplete(selectedItem.id, time_spent)
              }
              startTime={moduleStartTime.current}
              hasPrev={selectedIndex > 0}
              hasNext={selectedIndex < courseItems.length - 1}
            />
          ) : (
            <QuizPanel
              key={selectedItem.id}
              item={selectedItem}
              quizData={quizData}
              loadingContent={loadingContent}
              onPrev={goPrev}
              onNext={goNext}
              hasPrev={selectedIndex > 0}
              hasNext={selectedIndex < courseItems.length - 1}
              onComplete={() =>
                setCompletedIds((prev) => new Set([...prev, selectedItem.id]))
              }
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default ReadMore;
